import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { RouterProvider, createRouter } from '@tanstack/react-router';
import { isAxiosError } from 'axios';

import { AuthContext, AuthProvider } from '@/auth/context';
import { E2ETestAuthInit } from '@/auth/e2e-test-auth';

// Import the generated route tree
import { LDProvider } from '@/components/LDProvider';
import { CsrfProvider } from '@/lib/csrf-context';
import { getEnvironmentLevel } from '@/lib/common-utils';
import { WithAuthRole } from '@airreading/common';
import { useContext, useEffect, useMemo, useState } from 'react';
import { LoaderIcon } from 'lucide-react';
import { routeTree } from './routeTree.gen';

// Create a new router instance
const router = createRouter({
  routeTree,
  context: {
    actingUser: undefined as WithAuthRole | undefined,
  },
});

// Register the router instance for type safety
declare module '@tanstack/react-router' {
  interface Register {
    router: typeof router;
  }
}

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry(failureCount, error) {
        if (failureCount >= 3) return false;
        if (isAxiosError(error)) {
          // handle all non-retry cases.
          if (error.response?.status === 404) {
            return false;
          }
        }
        return true;
      },
    },
  },
});

function AppWithRouter() {
  const { actingUser, loading } = useContext(AuthContext);
  const context = useMemo(() => ({ actingUser }), [actingUser]);

  if (loading) {
    return (
      <div className="min-h-screen grid place-items-center p-10">
        <div className="flex justify-center">
          <LoaderIcon />
        </div>
      </div>
    );
  }

  return <RouterProvider router={router} context={context} />;
}

function App() {
  const [allowE2ETestAuth, setAllowE2ETestAuth] = useState(false);
  useEffect(() => {
    setAllowE2ETestAuth(typeof window !== 'undefined' && getEnvironmentLevel() !== 'production');
  }, []);

  return (
    <QueryClientProvider client={queryClient}>
      <AuthProvider>
        {allowE2ETestAuth && <E2ETestAuthInit />}
        <LDProvider>
          <CsrfProvider>
            <AppWithRouter />
          </CsrfProvider>
        </LDProvider>
      </AuthProvider>
    </QueryClientProvider>
  );
}

export default App;
